/**
 * Vaperite store map.
 *
 * One panel per province rather than a single national map. Eight of the ten
 * trading branches sit in Gauteng, so on a map of South Africa their pins
 * overlap and the name plates stack into an unreadable pile — the spread shows
 * but nothing is identifiable. Fitting each region to its own bounds keeps
 * every branch named and legible without anyone touching the zoom.
 *
 * Panels come from the data, so a branch in a new province adds a panel by
 * itself.
 *
 * Uses the same CARTO greyscale basemap as the customer map: a muted backdrop
 * keeps the pins and their labels readable, which a full-colour street map
 * does not.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper, Typography, Chip, Stack } from '@mui/material';
import { MapContainer, TileLayer, Marker, Popup, useMap, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useAdminTheme } from '../../admin-theme';
import { ACTIVE_VAPERITE_STORES, type VaperiteStore } from '../../data/vaperiteStores';

const PIN = '#06C167';

/** Region panels never zoom past this, so a lone branch doesn't land on rooftops. */
const MAX_REGION_ZOOM = 11;

/** Served from public/. */
const DEELIVER_MARK = '/deeliver-d.png';
/** Vaperite's official flame (transparent, from their own PDF, 1 Nov 2026). */
const VAPERITE_EMBLEM = '/vaperite-emblem.png';

/** Name plates carry both marks: the Deeliver D, then the Vaperite flame. */
const BADGES = `
  <img src="${DEELIVER_MARK}" alt="" onerror="this.style.display='none'"
       style="width:13px;height:13px;flex:0 0 13px;object-fit:contain;" />
  <img src="${VAPERITE_EMBLEM}" alt="" onerror="this.style.display='none'"
       style="width:7px;height:15px;flex:0 0 7px;object-fit:contain;" />`;

/**
 * Map plates use a shortened name. The full branch names carry "Shopping
 * Centre"/"Retail Centre" tails that roughly double the plate width, which is
 * what pushes neighbouring labels into each other around Johannesburg. The
 * table views still show the full name.
 */
const shortLabel = (branch: string) =>
  branch
    .replace(/^Vaperite\s+/, '')
    .replace(/\s+(Shopping Centre|Retail Centre|Shopping Center|Centre)$/i, '')
    .replace(/\s+Mall$/i, '');

const PLATE_H = 22;
/** Tried in order; first slot that clears everything already placed wins. */
const SLOTS = [0, -24, 24, -48, 48, -72, 72, -96, 96];
/** Close enough to measure against without laying the plate out for real. */
const plateWidth = (store: VaperiteStore) => shortLabel(store.branch).length * 6.2 + 52;

/**
 * A pin with its name plate beside it. The plate is absolutely positioned off
 * the 18px pin so the marker keeps a fixed anchor; `pointer-events: none` stops
 * the text stealing clicks meant for the pin.
 */
const createStoreIcon = (store: VaperiteStore, dark: boolean, side: 'left' | 'right', dy: number) => {
  /* `calc(-50% + -24px)` is invalid CSS and silently voids the whole transform,
     so the sign has to be part of the operator, not the value. */
  const sign = dy < 0 ? '-' : '+';

  const plate = `
    position:absolute;top:50%;
    transform:translateY(calc(-50% ${sign} ${Math.abs(dy)}px));
    ${side === 'right' ? 'left:24px;' : 'right:24px;'}
    pointer-events:none;white-space:nowrap;font-size:11px;font-weight:700;
    letter-spacing:.01em;padding:2px 6px;border-radius:5px;
    display:flex;align-items:center;gap:5px;
    color:${dark ? '#f2f2f3' : '#14141a'};
    background:${dark ? 'rgba(18,18,22,.92)' : 'rgba(255,255,255,.95)'};
    border:1px solid ${dark ? 'rgba(255,255,255,.14)' : 'rgba(0,0,0,.12)'};
    box-shadow:0 1px 4px rgba(0,0,0,.28);`;

  return L.divIcon({
    className: 'vaperite-store-marker',
    html: `
      <div style="position:relative;width:18px;height:18px;">
        <div style="
          position:absolute;inset:0;border-radius:50%;
          background:${PIN};border:3px solid ${dark ? '#0b0b0d' : '#ffffff'};
          box-shadow:0 0 0 2px ${PIN}66, 0 2px 6px rgba(0,0,0,.45);
        "></div>
        <div style="${plate}"><span>${shortLabel(store.branch)}</span>${BADGES}</div>
      </div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    popupAnchor: [0, -12],
  });
};

/**
 * Pushes labels off each other in screen space.
 *
 * Offsets derived from latitude alone don't work: whether two plates collide
 * depends on where their pins land in pixels, which depends on the fitted zoom.
 * Two branches a hair apart in latitude can still overlap, and two far apart
 * can be fine — so this measures the projected positions and walks the pins
 * top to bottom, giving each label the first slot that clears the ones already
 * placed.
 */
const deconflict = (map: L.Map, stores: VaperiteStore[], midLng: number) => {
  const placed: { l: number; r: number; t: number; b: number }[] = [];
  const out = new Map<string, number>();

  stores
    .map((s) => ({ s, p: map.latLngToContainerPoint([s.lat, s.lng]) }))
    .sort((a, b) => a.p.y - b.p.y)
    .forEach(({ s, p }) => {
      const w = plateWidth(s);
      const toRight = s.lng < midLng;
      const l = toRight ? p.x + 24 : p.x - 24 - w;
      const r = l + w;

      let chosen = SLOTS[SLOTS.length - 1];
      for (const dy of SLOTS) {
        const t = p.y + dy - PLATE_H / 2;
        const b = t + PLATE_H;
        const hits = placed.some(
          (q) => Math.min(q.r, r) - Math.max(q.l, l) > 2 && Math.min(q.b, b) - Math.max(q.t, t) > 2,
        );
        if (!hits) {
          chosen = dy;
          placed.push({ l, r, t, b });
          break;
        }
      }
      out.set(s.branch, chosen);
    });

  return out;
};

/**
 * Frames a panel on its own branches, then reports back where the labels should
 * sit.
 *
 * `invalidateSize()` first is load-bearing: these maps mount inside a CSS grid,
 * so Leaflet's first size measurement can predate the grid laying the panel
 * out. Fitting against that stale size silently frames the wrong area — and
 * `getBounds()` agrees with it, so it doesn't show up in a bounds check, only
 * on screen.
 */
const FitAndLabel = ({
  stores,
  midLng,
  onOffsets,
}: {
  stores: VaperiteStore[];
  midLng: number;
  onOffsets: (o: Map<string, number>) => void;
}) => {
  const map = useMap();
  const touched = useRef(false);

  useEffect(() => {
    if (!stores.length) return;

    const run = () => {
      map.invalidateSize({ animate: false });
      if (stores.length === 1) {
        map.setView([stores[0].lat, stores[0].lng], MAX_REGION_ZOOM);
      } else {
        map.fitBounds(L.latLngBounds(stores.map((s) => [s.lat, s.lng] as [number, number])), {
          padding: [55, 55],
          maxZoom: MAX_REGION_ZOOM,
          animate: false,
        });
      }
      onOffsets(deconflict(map, stores, midLng));
    };

    const frame = requestAnimationFrame(run);

    /* Re-fit while the panel is still settling (grid reflow, sidebar toggle,
       window resize). Once the user has zoomed we leave their view alone, but
       still re-place the labels for the new projection. */
    const markTouched = () => { touched.current = true; };
    const relabel = () => onOffsets(deconflict(map, stores, midLng));

    map.on('zoomstart dragstart', markTouched);
    map.on('zoomend moveend', relabel);

    const ro = new ResizeObserver(() => { if (!touched.current) run(); });
    ro.observe(map.getContainer());

    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
      map.off('zoomstart dragstart', markTouched);
      map.off('zoomend moveend', relabel);
    };
  }, [map, stores, midLng, onOffsets]);

  return null;
};

const StorePopup = ({ store }: { store: VaperiteStore }) => (
  <Box sx={{ minWidth: 210 }}>
    <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', mb: 0.5 }}>{store.branch}</Typography>
    <Typography sx={{ fontSize: '0.78rem', color: '#555', mb: 0.75 }}>
      {store.address}
      <br />
      {store.city}, {store.province} {store.zip}
    </Typography>
    {store.tradingHours && (
      <Typography sx={{ fontSize: '0.75rem', color: '#333' }}>
        <strong>Hours</strong>
        <br />
        {store.tradingHours.map((slot) => (
          <span key={slot.days}>
            {slot.days} {slot.open}–{slot.close}
            <br />
          </span>
        ))}
      </Typography>
    )}
    <Typography sx={{ fontSize: '0.75rem', mt: 0.75 }}>
      <a href={`tel:${store.phone}`}>{store.phone}</a>
      {store.email && (
        <>
          <br />
          <a href={`mailto:${store.email}`}>{store.email}</a>
        </>
      )}
    </Typography>
  </Box>
);

const RegionPanel = ({ province, stores }: { province: string; stores: VaperiteStore[] }) => {
  const { colors, cardStyle, mode } = useAdminTheme();
  const dark = mode === 'dark';
  const [offsets, setOffsets] = useState<Map<string, number>>(new Map());

  /* Esri's Gray Canvas: a muted base layer plus a separate place-label layer,
     free with attribution and no API key. This was CARTO Positron/Dark Matter,
     which CARTO now serves with an "API KEY REQUIRED" watermark stamped across
     every tile (2026-09-16). Esri's tile scheme is {z}/{y}/{x}. */
  const canvas = dark ? 'World_Dark_Gray' : 'World_Light_Gray';
  const tileUrl = `https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/${canvas}_Base/MapServer/tile/{z}/{y}/{x}`;
  const labelUrl = `https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/${canvas}_Reference/MapServer/tile/{z}/{y}/{x}`;

  /* Branches east of the region's midpoint put their plate on the pin's left,
     so labels grow away from the middle rather than across each other. */
  const midLng = useMemo(() => stores.reduce((n, s) => n + s.lng, 0) / stores.length, [stores]);

  /* Only re-render markers when the placement actually changed, otherwise the
     moveend handler and the state update chase each other. */
  const applyOffsets = useCallback((next: Map<string, number>) => {
    setOffsets((prev) => {
      if (prev.size === next.size && [...next].every(([k, v]) => prev.get(k) === v)) return prev;
      return next;
    });
  }, []);

  return (
    <Paper sx={{ ...cardStyle, borderRadius: 2, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          px: 2, py: 1.25,
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
        }}
      >
        <Typography sx={{ color: colors.textPrimary, fontWeight: 800, fontSize: '0.9rem' }}>
          {province}
        </Typography>
        <Chip
          size="small"
          label={`${stores.length} ${stores.length === 1 ? 'branch' : 'branches'}`}
          sx={{ bgcolor: `${colors.info}1a`, color: colors.info, fontSize: '0.7rem', height: 22 }}
        />
      </Box>

      <Box
        sx={{
          height: 420,
          '& .leaflet-container': { background: dark ? '#101014' : '#eceff3' },
          /* Leaflet sizes divIcons to iconSize and clips; the name plate sits
             outside that box, so it has to be allowed to overflow. */
          '& .vaperite-store-marker': { overflow: 'visible !important' },
        }}
      >
        <MapContainer center={[-28.5, 24.5]} zoom={5} zoomControl={false} style={{ height: '100%', width: '100%' }}>
          <TileLayer url={tileUrl} attribution="Tiles &copy; Esri" maxNativeZoom={16} />
          <TileLayer url={labelUrl} maxNativeZoom={16} />
          <ZoomControl position="bottomright" />
          <FitAndLabel stores={stores} midLng={midLng} onOffsets={applyOffsets} />
          {stores.map((store) => (
            <Marker
              key={store.branch}
              position={[store.lat, store.lng]}
              icon={createStoreIcon(
                store,
                dark,
                store.lng >= midLng ? 'left' : 'right',
                offsets.get(store.branch) ?? 0,
              )}
            >
              <Popup>
                <StorePopup store={store} />
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </Box>
    </Paper>
  );
};

export const VaperiteStoreMap = () => {
  const { colors } = useAdminTheme();
  const stores = ACTIVE_VAPERITE_STORES;

  /** West to east, so the Cape panels read left of the inland ones. */
  const regions = useMemo(() => {
    const by = new Map<string, VaperiteStore[]>();
    stores.forEach((s) => by.set(s.province, [...(by.get(s.province) ?? []), s]));
    const meanLng = (list: VaperiteStore[]) => list.reduce((n, s) => n + s.lng, 0) / list.length;
    return [...by.entries()].sort((a, b) => meanLng(a[1]) - meanLng(b[1]));
  }, [stores]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography variant="body2" sx={{ color: colors.textSecondary }}>
          {stores.length} branches trading with Deeliver, across {regions.length}{' '}
          {regions.length === 1 ? 'province' : 'provinces'}
        </Typography>
      </Stack>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(440px, 1fr))' }}>
        {regions.map(([province, regionStores]) => (
          <RegionPanel key={province} province={province} stores={regionStores} />
        ))}
      </Box>
    </Box>
  );
};

export default VaperiteStoreMap;
